<template>
  <div class="wagers-container">
    <!-- Desktop Table View -->
    <div class="wagers-card desktop-only">
      <div class="table-container">
        <table class="wagers-table">
          <thead>
            <tr>
              <th>{{ $t('wagers.betslipId') }}</th>
              <th>{{ $t('wagers.date') }}</th>
              <th>{{ $t('wagers.reference') }}</th>
              <th>{{ $t('wagers.description') }}</th>
              <th>{{ $t('wagers.status') }}</th>
              <th>{{ $t('wagers.totalBetAmount') }}</th>
              <th>{{ $t('wagers.totalReturn') }}</th>
              <th>{{ $t('wagers.detailsText') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="loader-cell">
                <div class="table-loader">
                  <span class="spinner"></span>
                  {{ $t('wagers.loadingTransactions') }}
                </div>
              </td>
            </tr>
            <template v-else-if="tableRows.length === 0">
              <tr>
                <td colspan="8" class="empty-cell">{{ $t('wagers.noTransactions') }}</td>
              </tr>
            </template>
            <template v-else>
              <template v-for="item in tableRows" :key="item.id">
                <tr class="transaction-row">
                  <td>{{ item.id }}</td>
                  <td>{{ formatDate(item.created_at) }}</td>
                  <td>{{ item.reference }}</td>
                  <td>{{ $t('wagers.wager') }}</td>
                  <td>
                    <span :class="['status', item.bet_status.toLowerCase(), getStatusClass(item)]">
                      {{ item.bet_status }}
                    </span>
                  </td>
                  <td>{{ item.currency_symbol }} {{ item.total }}</td>
                  <td>{{ getTotalReturn(item) }}</td>
                  <td>
                    <button class="details-button" @click.stop="$emit('view-details', item)">
                      {{ $t('wagers.detailsText') }}
                    </button>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div class="mobile-cards mobile-only">
      <div v-if="loading" class="loading-cards">
        <div class="card-loader">
          <span class="spinner"></span>
          {{ $t('wagers.loadingTransactions') }}
        </div>
      </div>
      <template v-else-if="tableRows.length === 0">
        <div class="empty-cards">
          <p>{{ $t('wagers.noTransactions') }}</p>
        </div>
      </template>
      <template v-else>
        <WagerCard
          v-for="item in tableRows"
          :key="item.id"
          :transaction="item"
          @view-details="$emit('view-details', $event)"
          @rebet="handleRebet"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed } from 'vue'
import type { Transaction } from '@/stores/transactions'
import WagerCard from './WagerCard.vue'
import { formatCurrency } from '@/utils/currency'

const props = defineProps<{
  transactions: Transaction[]
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'view-details', wager: Transaction): void
  (e: 'rebet'): void
}>()

const tableRows = computed(() => {
  return props.transactions
})

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const handleRebet = () => {
  emit('rebet')
}

import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const getTotalReturn = (item: Transaction) => {
  if (item.bet_status.toLowerCase() === 'open') {
    return t('wagers.tbc')
  }
  return formatCurrency(item.total_winnings, item.currency_symbol)
}

const getStatusClass = (item: Transaction) => {
  if (item.bet_status.toLowerCase() === 'settled' && item.total_winnings === '0.00') {
    return 'settled-zero'
  }
  return ''
}
</script>

<style scoped>
.wagers-container {
  width: 100%;
}

.wagers-card {
  background: var(--bg-card);
  flex: 1;
  overflow: hidden;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

.mobile-cards {
  padding: 0;
}

.loading-cards,
.empty-cards {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-primary);
}

.card-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.mobile-details {
  margin-top: 16px;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.table-container::-webkit-scrollbar {
  display: none;
}

.wagers-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
  margin: 0 auto;
}

.wagers-table th {
  background: var(--bg-table-header, var(--bg-tertiary));
  color: var(--text-secondary);
  font-weight: normal;
  text-align: center;
  padding: 16px;
  font-size: 14px;
  border-bottom: 1px solid var(--border-color);
}

.wagers-table td {
  background: var(--bg-table-body, var(--bg-tertiary));
  padding: 16px;
  color: var(--text-primary);
  font-size: 14px;
  border-bottom: 1px solid var(--border-light);
  text-align: center;
}

.wagers-table tr:last-child td {
  border-bottom: none;
}

.wagers-table tr:hover {
  background: var(--bg-tertiary);
}

.transaction-row {
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.transaction-row:hover {
  background: var(--bg-tertiary);
  border-left-color: var(--accent-color);
}

.transaction-row.expanded {
  background: var(--bg-tertiary);
  /* border-left-color: var(--accent-color); */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status.open {
  color: var(--bg-warning);
}

.status.settled {
  color: var(--bg-success);
}

.status.cancelled {
  color: var(--bg-danger);
}

.status.settled-zero {
  color: var(--bg-danger);
}

.details-button {
  background: none;
  border: none;
  color: var(--text-details-button, var(--text-primary));
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
  padding: 0;
  transition: color 0.2s;
}

.details-button:hover {
  color: var(--text-secondary);
}

.details-row {
  background: var(--bg-secondary);
  /* border-top: 2px solid var(--accent-color); */
}

.details-cell {
  padding: 0 !important;
  background: var(--bg-secondary);
}

.transaction-details-container {
  background: var(--bg-card);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.loader-cell {
  text-align: center;
  padding: 40px 0;
  color: var(--text-primary);
  font-size: 16px;
}

.table-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 4px solid var(--border-light);
  border-top: 4px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-cell {
  text-align: center;
  color: var(--text-light);
  font-size: 16px;
  padding: 40px 0;
}

/* Large screen improvements */
@media (min-width: 1200px) {
  .transaction-details-container {
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  .wagers-table {
    min-width: 1000px;
  }
  
  .wagers-table th,
  .wagers-table td {
    padding: 20px 16px;
    font-size: 15px;
  }
  
  .transaction-row.expanded {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: block;
  }

  .wagers-table th,
  .wagers-table td {
    padding: 12px;
    font-size: 13px;
    white-space: nowrap;
  }

  .status {
    padding: 3px 6px;
    font-size: 11px;
  }

  .details-button {
    font-size: 13px;
  }
  
  .transaction-details-container {
    margin: 8px;
    border-radius: 8px;
  }
}
</style> 